import { useCallback, useEffect, useRef, useState } from 'react';
import { errorMessage } from './cloudApi';

/**
 * Load data on mount and on demand. The portal's pages are small and
 * independent, so this stands in for a query library.
 */
export function useAsync<T>(load: () => Promise<T>, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const latest = useRef(0);

  const reload = useCallback(async () => {
    const run = ++latest.current;
    setLoading(true);
    try {
      const result = await load();
      if (run === latest.current) {
        setData(result);
        setError(null);
      }
    } catch (err) {
      if (run === latest.current) setError(errorMessage(err));
    } finally {
      if (run === latest.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { data, error, loading, reload, setData };
}
